import { useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { formatApiErrorDetail } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-0 border-b border-fc-ink/20 py-3 text-fc-ink placeholder:text-fc-ink2/50 focus:outline-none focus:border-fc-cobalt transition-colors";

export default function AdminResetPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen surface-light flex items-center justify-center px-5">
      <form data-testid="admin-reset-form" onSubmit={submit} className="w-full max-w-md bg-white border border-fc-ink/10 shadow-[0_30px_70px_rgba(11,44,92,0.12)] p-8 sm:p-10">
        <div className="inline-block bg-white rounded-lg border border-fc-ink/10 p-2 mb-6">
          <img src="/fc-logo.png" alt="F&C Air Conditioning" className="h-10 w-auto" />
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-fc-ink">Choose a new password</h1>
        {!token && !done && (
          <p data-testid="reset-no-token" className="mt-4 text-sm font-semibold text-red-600">This reset link is missing its token. Please request a new one.</p>
        )}
        {done ? (
          <>
            <p data-testid="reset-success" className="mt-4 text-sm text-fc-ink2">Your password has been updated. You can sign in with it now.</p>
            <button
              type="button"
              data-testid="reset-goto-login"
              onClick={() => navigate("/admin")}
              className="mt-8 w-full bg-fc-orange text-white py-4 font-bold uppercase tracking-wide text-sm hover:brightness-95 transition-all"
            >
              Go to Sign In
            </button>
          </>
        ) : (
          <>
            <div className="mt-8 space-y-6">
              <div>
                <label htmlFor="reset-password" className="font-mono text-[10px] uppercase tracking-widest text-fc-ink2">New Password</label>
                <input id="reset-password" data-testid="reset-password-input" type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
              </div>
              <div>
                <label htmlFor="reset-confirm" className="font-mono text-[10px] uppercase tracking-widest text-fc-ink2">Confirm Password</label>
                <input id="reset-confirm" data-testid="reset-confirm-input" type="password" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat it" />
              </div>
            </div>
            {error && <div data-testid="reset-error" className="mt-5 text-sm font-semibold text-red-600">{error}</div>}
            <button
              data-testid="reset-submit"
              type="submit"
              disabled={loading || !token}
              className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-fc-orange text-white py-4 font-bold uppercase tracking-wide text-sm hover:brightness-95 transition-all disabled:opacity-60"
            >
              {loading ? <><Loader2 size={18} className="animate-spin" /> Updating…</> : "Update Password"}
            </button>
          </>
        )}
        <Link to="/admin" data-testid="reset-back-login" className="mt-5 block text-center font-mono text-[11px] uppercase tracking-widest text-fc-ink2 hover:text-fc-ink">
          ← Back to sign in
        </Link>
      </form>
    </div>
  );
}
